import React, { useState } from "react";

import { Alert, Box, CircularProgress, Grid, TextField } from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import {
  OrderBetween,
  Replacement,
} from "@safetyculture/s12-apis-connect-web/s12/accounts/admin/v1/white_label_pb";

import { UpdateWhiteLabelReplacement } from "api/endpoints/custom-labels";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { GrpcError } from "context/snackbar-context";
import { UrlParamTypes } from "types/org-display";

export interface CustomerLabelEditProps {
  replacement: Replacement;
  order: OrderBetween;
  onCancel: (event: object, reason: string) => void;
  onComplete: () => void;
}

const CustomerLabelEdit = ({
  replacement,
  order,
  onCancel,
  onComplete,
}: CustomerLabelEditProps) => {
  const classes = mainStyles();
  useParams<UrlParamTypes>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<GrpcError>();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [searchValue, setSearchValue] = useState(replacement.searchValue);
  const [replaceValue, setReplaceValue] = useState(replacement.replaceValue);

  const updateReplacement = async () => {
    try {
      setIsProcessing(true);
      await UpdateWhiteLabelReplacement(
        {
          orgId: roleId,
          id: replacement.id,
          searchValue: searchValue,
          replaceValue: replaceValue,
          order: order,
        },
        callOptionsHeaders(regionCode, roleId)
      );
      onComplete();
    } catch (e) {
      setError(e as GrpcError);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderErrorMessage = () => {
    if (!error) {
      return;
    }
    return (
      <Alert severity="error" variant="filled" data-test-id="error-message">
        {error?.message}
      </Alert>
    );
  };

  return (
    <>
      <ConfirmDialog
        title="Edit Customer Label"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={replacement != undefined}
        setOpen={() => {}}
        onConfirm={() => {
          setIsProcessing(true);
          updateReplacement();
        }}
        onCancel={() => onCancel({}, "cancel")}
        disabled={isProcessing}
        disableCancelBtn={isProcessing}
      >
        {isProcessing ? (
          <Grid
            container
            justifyContent="center"
            className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
          >
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
              data-testid="loading-icon"
            />
          </Grid>
        ) : (
          <>
            <Box
              sx={{ width: "100%", paddingTop: "0.5rem" }}
              className={clsx(classes.spaceTopSmall)}
            >
              <TextField
                inputProps={{
                  "data-testid": "enter-custom-label-search-value-field",
                }}
                fullWidth
                margin="dense"
                label="Enter search value"
                variant={"outlined"}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                disabled={isProcessing}
              />
              <TextField
                inputProps={{
                  "data-testid": "enter-custom-label-replace-value-field",
                }}
                fullWidth
                margin="dense"
                label="Enter replace value"
                variant={"outlined"}
                value={replaceValue}
                onChange={(e) => setReplaceValue(e.target.value)}
                disabled={isProcessing}
              />
            </Box>
            <Box
              sx={{ width: "100%", paddingTop: "0.5rem" }}
              className={clsx(classes.spaceTopSmall)}
            >
              {renderErrorMessage()}
            </Box>
          </>
        )}
      </ConfirmDialog>
    </>
  );
};

export default CustomerLabelEdit;
